import {
  Entity,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
} from '@backstage/catalog-model';
import { MarkdownContent } from '@backstage/core-components';
import type { JsonValue } from '@backstage/types';
import { AboutField } from '@backstage/plugin-catalog';
import { Grid } from '@backstage/ui';
import { makeStyles } from '@material-ui/core/styles';
import {
  EntityRefLinks,
  getEntityRelations,
} from '@backstage/plugin-catalog-react';
import Chip from '@material-ui/core/Chip';
import { isNil, toString } from 'lodash';

const useStyles = makeStyles({
  description: {
    wordBreak: 'break-word',
  },
});

export interface AboutContentProps {
  entity: Entity;
}

export function MyAboutContent(props: AboutContentProps) {
  const { entity } = props;
  const classes = useStyles();

  const isSystem = entity.kind.toLocaleLowerCase('en-US') === 'system';
  const isResource = entity.kind.toLocaleLowerCase('en-US') === 'resource';
  const isComponent = entity.kind.toLocaleLowerCase('en-US') === 'component';
  const isAPI = entity.kind.toLocaleLowerCase('en-US') === 'api';
  const isTemplate = entity.kind.toLocaleLowerCase('en-US') === 'template';
  const isGroup = entity.kind.toLocaleLowerCase('en-US') === 'group';

  const partOfSystemRelations = getEntityRelations(entity, RELATION_PART_OF, {
    kind: 'system',
  });
  const partOfComponentRelations = getEntityRelations(
    entity,
    RELATION_PART_OF,
    {
      kind: 'component',
    },
  );
  const partOfDomainRelations = getEntityRelations(entity, RELATION_PART_OF, {
    kind: 'domain',
  });
  const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);

  const getFieldValueIfString = (field: JsonValue | undefined) =>
    typeof field === 'string' ? field : undefined;

  const getFieldValueIfBoolean = (field: JsonValue | undefined) => {
    if (typeof field !== 'boolean') {
      return 'Unknown';
    }
    return field ? 'True' : 'False';
  };

  const tier = entity.spec?.tier || entity.metadata?.annotations?.tier;

  const columns = { initial: '1', sm: '2', lg: '3' } as const;

  return (
    <Grid.Root columns={columns} gap="3">
      <Grid.Item colSpan={columns}>
        <AboutField label="Description">
          <MarkdownContent
            className={classes.description}
            content={
              entity?.metadata?.description || 'No description available'
            }
          />
        </AboutField>
      </Grid.Item>
      <AboutField label="Owner" value="No Owner">
        {ownedByRelations.length > 0 && (
          <EntityRefLinks entityRefs={ownedByRelations} defaultKind="group" />
        )}
      </AboutField>
      {(isSystem || partOfDomainRelations.length > 0) && (
        <AboutField label="Domain" value="No Domain">
          {partOfDomainRelations.length > 0 && (
            <EntityRefLinks
              entityRefs={partOfDomainRelations}
              defaultKind="domain"
            />
          )}
        </AboutField>
      )}
      {(isAPI ||
        isComponent ||
        isResource ||
        partOfSystemRelations.length > 0) && (
        <AboutField label="System" value="No System">
          {partOfSystemRelations.length > 0 && (
            <EntityRefLinks
              entityRefs={partOfSystemRelations}
              defaultKind="system"
            />
          )}
        </AboutField>
      )}
      {isComponent && partOfComponentRelations.length > 0 && (
        <AboutField label="Parent Component" value="No Parent Component">
          <EntityRefLinks
            entityRefs={partOfComponentRelations}
            defaultKind="component"
          />
        </AboutField>
      )}
      {(isAPI ||
        isComponent ||
        isResource ||
        isTemplate ||
        isGroup ||
        !!entity?.spec?.type) && (
        <AboutField
          label="Type"
          value={getFieldValueIfString(entity?.spec?.type)}
        />
      )}
      {(isAPI || isComponent || !!entity?.spec?.lifecycle) && (
        <AboutField
          label="Lifecycle"
          value={getFieldValueIfString(entity?.spec?.lifecycle)}
        />
      )}

      {(isAPI || !!entity?.spec?.audience) && (
        <AboutField
          label="Audience"
          value={getFieldValueIfString(entity.spec?.audience) ?? 'No Audience'}
        />
      )}
      {(isComponent || !!entity?.spec?.tier) && (
        <AboutField
          label="Tier"
          value={isNil(tier) ? 'No Tier' : toString(tier)}
        />
      )}
      {(isComponent || !!entity?.spec?.partOfCoreProduct) && (
        <AboutField
          label="Part of Core Product"
          value={getFieldValueIfBoolean(entity?.spec?.partOfCoreProduct)}
        />
      )}
      <AboutField label="Tags" value="No Tags">
        {(entity?.metadata?.tags || []).map(tag => (
          <Chip key={tag} size="small" label={tag} />
        ))}
      </AboutField>
    </Grid.Root>
  );
}
